import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  BarChart3,
  Factory,
  Leaf,
  Scale,
  TrendingUp,
  Users,
  Building2,
  Route,
  ArrowRight,
  Boxes,
  Gavel,
} from "lucide-react";
import entregablesData from "@/data/entregables.json";

const iconMap = {
  BarChart3,
  Factory,
  Leaf,
  Scale,
  TrendingUp,
  Users,
  Building2,
  Route,
  Boxes,
  Gavel,
};

export default function Entregables() {
  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Entregables del Estudio | SOSTRE BALEAR</title>
        <meta name="description" content="Los 8 work packages del estudio de viabilidad SOSTRE BALEAR: mercado, construcción, industrialización, sostenibilidad medioambiental, técnica, legal, financiera y social." />
        <link rel="canonical" href="https://www.sostrebalear.com/entregables" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Entregables del Estudio | SOSTRE BALEAR" />
        <meta property="og:description" content="Los 8 entregables del estudio de viabilidad para vivienda asequible en Baleares." />
        <meta property="og:url" content="https://www.sostrebalear.com/entregables" />
        <meta property="og:image" content="https://www.sostrebalear.com/assets/logos/sostre-balear.png" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:site_name" content="SOSTRE BALEAR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Entregables del Estudio | SOSTRE BALEAR" />
        <meta name="twitter:description" content="Los 8 entregables del estudio de viabilidad para vivienda asequible en Baleares." />
      </Helmet>
      {/* Header */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-medium tracking-wide uppercase text-[#22c55e]">
              Work Packages
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3 tracking-tight">
              Entregables del Estudio
            </h1>
            <p className="text-lg text-slate-600 mt-6 leading-relaxed">
              El estudio SOSTRE BALEAR se estructura en 8 entregables (Work
              Packages) que analizan cada dimensión de viabilidad del modelo
              propuesto.
            </p>
          </div>
        </div>
      </section>

      {/* Grid de Entregables */}
      <section className="py-16 md:py-24" data-testid="entregables-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {entregablesData.map((wp, index) => {
              const IconComponent = iconMap[wp.icono] || BarChart3;
              return (
                <Link
                  key={wp.id}
                  to={`/entregables/${wp.id}`}
                  className="group bg-white rounded-2xl border border-slate-100 p-6 hover:border-[#22c55e]/30 hover:shadow-lg transition-all duration-300"
                  data-testid={`wp-card-${wp.id}`}
                  style={{
                    animationDelay: `${index * 0.1}s`,
                  }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                      style={{ backgroundColor: `${wp.color}15` }}
                    >
                      <IconComponent
                        size={24}
                        style={{ color: wp.color }}
                      />
                    </div>
                    <span className="text-4xl font-bold text-slate-100 group-hover:text-slate-200 transition-colors">
                      {String(wp.numero).padStart(2, "0")}
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-[#22c55e] transition-colors">
                    {wp.titulo}
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">{wp.subtitulo}</p>

                  <div className="flex items-center gap-2 text-sm font-medium text-[#22c55e] opacity-0 group-hover:opacity-100 transition-opacity">
                    Ver detalle
                    <ArrowRight
                      size={16}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            ¿Quieres el documento completo?
          </h2>
          <p className="text-slate-600 mb-8">
            Descarga el resumen ejecutivo y la presentación del estudio SOSTRE
            BALEAR.
          </p>
          <Link
            to="/documentacion"
            className="inline-flex items-center gap-2 bg-[#22c55e] hover:bg-[#16a34a] text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg hover:shadow-xl"
            data-testid="cta-documentacion"
          >
            Ir a Documentación
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}
